import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Overline, SectionHeader, Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { INDUSTRIES } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const IndustriesHero = () => (
    <section className="relative overflow-hidden pt-44 pb-20 sm:pb-24" data-testid="industries-hero">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute -top-24 left-1/4 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                <Overline>Industries</Overline>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                {['Fluent in the risks', 'of your sector.'].map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                        >
                            {i === 1 ? <>of <span className="text-deepteal">your sector.</span></> : line}
                        </motion.span>
                    </span>
                ))}
            </h1>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
                We have supported organizations across eight major industries. Select your sector to see the
                resilience challenges we encounter most — and how we address them.
            </motion.p>
        </div>
    </section>
);

const IndustryMatrix = () => {
    const [active, setActive] = useState(INDUSTRIES[0].id);
    const industry = INDUSTRIES.find((i) => i.id === active);

    return (
        <section className="pb-20 sm:pb-28" data-testid="industry-matrix-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" role="tablist" aria-label="Select industry">
                    {INDUSTRIES.map((ind) => (
                        <button
                            key={ind.id}
                            role="tab"
                            aria-selected={active === ind.id}
                            data-testid={`industry-tab-${ind.id}`}
                            onClick={() => setActive(ind.id)}
                            className={`flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all duration-200 ${active === ind.id ? 'border-deepteal/50 bg-deepteal/10' : 'border-slate-800 bg-navy-800/50 hover:border-slate-400'}`}
                        >
                            <ind.icon size={20} className={active === ind.id ? 'text-deepteal' : 'text-slate-500'} />
                            <span className={`font-display text-sm sm:text-base font-semibold ${active === ind.id ? 'text-deepteal' : 'text-slate-200'}`}>
                                {ind.name}
                            </span>
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={industry.id}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="mt-10 overflow-hidden rounded-2xl border border-slate-800 bg-navy-800/40"
                        data-testid="industry-detail-panel"
                    >
                        <div className="border-b border-slate-800 p-8 sm:p-10">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-deepteal">{industry.name}</p>
                            <p className="mt-3 max-w-2xl font-display text-xl sm:text-2xl font-semibold leading-snug text-slate-100 text-balance">
                                {industry.intro}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2">
                            <div className="border-b border-slate-800 p-8 sm:p-10 md:border-b-0 md:border-r">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle size={15} className="text-amber-600" />
                                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Common challenges</h3>
                                </div>
                                <ul className="mt-6 space-y-4">
                                    {industry.challenges.map((c, i) => (
                                        <motion.li
                                            key={c}
                                            initial={{ opacity: 0, x: 14 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: 0.1 + i * 0.07, ease: EASE }}
                                            className="flex gap-3 text-sm leading-relaxed text-slate-300"
                                        >
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber-500" />
                                            {c}
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                            <div className="p-8 sm:p-10">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={15} className="text-deepteal" />
                                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">How Planmodo helps</h3>
                                </div>
                                <ul className="mt-6 space-y-4">
                                    {industry.helps.map((h, i) => (
                                        <motion.li
                                            key={h}
                                            initial={{ opacity: 0, x: 14 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: 0.15 + i * 0.07, ease: EASE }}
                                            className="flex gap-3 text-sm leading-relaxed text-slate-300"
                                        >
                                            <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-deepteal/80" />
                                            {h}
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-slate-800 p-8 sm:px-10">
                            <Link
                                to="/contact"
                                data-testid="industry-cta-link"
                                className="group inline-flex items-center gap-2 text-sm font-semibold text-deepteal"
                            >
                                Discuss resilience in {industry.name.toLowerCase()}
                                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

const Standards = () => (
    <section className="border-t border-slate-800 bg-navy-950 py-20 sm:py-28" data-testid="standards-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                overline="Cross-sector standards"
                title="Aligned to the frameworks your regulators and auditors recognize."
                description="Our methods align with ISO 22301, ISO 27001, ISO/IEC 27031, NIST SP 800-34, the NIST Cybersecurity Framework, and sector-specific resilience regulation — so the work we deliver stands up to external review."
            />
            <div className="mt-12 flex flex-wrap gap-3">
                {['ISO 22301', 'ISO 27001', 'ISO/IEC 27031', 'NIST CSF', 'NIST SP 800-34', 'NFPA 1600', 'DORA', 'BCI Good Practice'].map((s, i) => (
                    <Reveal key={s} delay={i * 0.05}>
                        <span className="rounded-full border border-slate-700 bg-navy-800/60 px-5 py-2.5 font-mono text-xs tracking-wide text-slate-300" data-testid={`standard-badge-${i}`}>
                            {s}
                        </span>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function Industries() {
    useSEO(
        'Industries We Serve | Planmodo',
        'Resilience and recovery services for healthcare, financial services, technology, manufacturing, retail, logistics, professional services and the public sector.'
    );
    return (
        <>
            <IndustriesHero />
            <IndustryMatrix />
            <Standards />
            <CTASection
                title="Your sector has its own definition of critical."
                text="Tell us about your operating environment. We will map your most plausible disruption scenarios and the capability you need to withstand them."
            />
        </>
    );
}
